/**
 * Marine vessel marker styling (L.2.2, R.1d render). Per §3B.8 the marine family
 * is the AMBIENT register — desaturated, never pulse; SHAPE (vesselSilhouettes)
 * encodes the hull kind, COLOR stays a single ambient marine hue. Freshness is
 * conveyed by OPACITY keyed to ais_visibility (Class-B / AIS-dark gaps are normal,
 * so a sparse hull is dimmed, not marked offline) plus a "live feed stale" flag
 * when a position is older than its visibility class expects.
 */
import type { VesselProperties } from './api';

/** Ambient marine hue (desaturated cyan-slate) — distinct from the act-now palettes. */
export const VESSEL_AMBIENT_COLOR = '#6fb3c4';
export const VESSEL_MARKER_SIZE = 18;

/** Base opacity by AIS-visibility rating (reliability → freshness expectation). */
export function vesselOpacity(aisVisibility: string | null): number {
  switch (aisVisibility) {
    case 'reliable':
      return 1.0;
    case 'intermittent':
      return 0.7;
    case 'discover_live':
      return 0.6;
    case 'dark_risk':
      return 0.5;
    case 'none_static':
      return 0.45;
    default:
      return 0.7;
  }
}

/** Per-visibility staleness window (minutes). Beyond this the marker carries a
 *  "live feed stale" note — but a sparse Class-B / RHIB hull updating every few
 *  minutes is NOT stale, so its window is generous. */
function staleWindowMin(aisVisibility: string | null): number {
  switch (aisVisibility) {
    case 'reliable':
      return 30;
    case 'intermittent':
      return 90;
    default:
      return 240;
  }
}

export interface VesselStyle {
  color: string;
  size: number;
  opacity: number;
  /** true → render a "live feed stale" badge (last position older than expected). */
  stale: boolean;
}

export function classifyVessel(p: VesselProperties, nowMs: number = Date.now()): VesselStyle {
  let stale = false;
  if (p.last_seen_at) {
    const ageMin = (nowMs - Date.parse(p.last_seen_at)) / 60_000;
    stale = Number.isFinite(ageMin) && ageMin > staleWindowMin(p.ais_visibility);
  }
  return {
    color: VESSEL_AMBIENT_COLOR,
    size: VESSEL_MARKER_SIZE,
    // A stale hull dims further so the operator reads "old fix" at a glance.
    opacity: stale ? Math.min(vesselOpacity(p.ais_visibility), 0.4) : vesselOpacity(p.ais_visibility),
    stale,
  };
}
